import { invariant } from '@e-socna/utils';
import { getFragmentData } from '@e-socna/data-layer';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { Wizard } from '../../components/Wizard';

import { FormVariant } from '../enums/variant.enum';

import { useFolderSteps } from './hooks/useSteps.hook';
import { FolderForm_Folder_Fragment } from './fragments/folder.fragment';
import { useFolderSubmit } from './hooks/useSubmit.hook';
import { useInitialValuesFactory } from './hooks/useInitialValuesFactory.hook';
import type { FolderMutationResult } from './hooks/useSubmit.hook';
import type { FolderFormTab } from './enums/tab.enum';

interface Props {
  readonly askMemoBeforeSubmit?: boolean;
  readonly folderId?: Id;
  readonly formVariant: FormVariant;
  readonly initialValues?: FragmentType<typeof FolderForm_Folder_Fragment>;
  readonly onCancel?: () => void;
  readonly onSuccess?: (result: FolderMutationResult) => void;
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly showCancelButton?: boolean;
  readonly stepIdsSubset?: readonly FolderFormTab[];
  readonly title?: string;
}

export const FolderForm = ({
  askMemoBeforeSubmit = true,
  folderId,
  formVariant,
  initialValues: ppInitialValues,
  onCancel: handleCancel,
  onSuccess: handleSuccess,
  setMustBlockAction,
  showCancelButton = true,
  stepIdsSubset,
  title,
}: Readonly<Props>) => {
  invariant(
    !!(formVariant !== FormVariant.Modification || folderId),
    'You must provide a folderId when formVariant is Modification',
  );

  const pInitialValues = getFragmentData(
    FolderForm_Folder_Fragment,
    ppInitialValues,
  );

  const { initialValues } = useInitialValuesFactory(pInitialValues);

  const { handleSubmit } = useFolderSubmit({
    folderId,
    formVariant,
    handleSuccess,
  });

  const { steps, wizardChildren } = useFolderSteps({
    formVariant,
    stepIdsSubset,
  });

  return (
    <Wizard
      askMemoBeforeSubmit={askMemoBeforeSubmit}
      formTitle={title}
      formVariant={formVariant}
      initialValues={initialValues}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      setMustBlockAction={setMustBlockAction}
      showCancelButton={showCancelButton}
      steps={steps}
    >
      {wizardChildren}
    </Wizard>
  );
};
